/**
 * MFX OS — Sales Orders (blueprint Stage 3). Independent status tracks per
 * order: Commercial / Production / Fulfillment / Invoice / Payment — never
 * one collapsed "Open" field. Shows only OS-chain SOs (source mfx-os);
 * legacy portal SOs continue to live in the OS app unchanged.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOsList, usd, fmtDate } from '../../os/core';
import { Chip, Tracks } from '../../os/ui';
import type { SalesOrderOS } from '../../os/types';

export default function SalesOrders() {
  const nav = useNavigate();
  const { rows, loading } = useOsList<SalesOrderOS>('salesOrders', { wheres: [['source', '==', 'mfx-os']], order: ['createdAt', 'desc'] });
  const [q, setQ] = useState('');

  const shown = useMemo(() => {
    if (!q.trim()) return rows;
    const t = q.toLowerCase();
    return rows.filter((x) => `${x.soNum} ${x.customerName} ${x.poNumber}`.toLowerCase().includes(t));
  }, [rows, q]);

  const openProd = rows.filter((x) => x.tracks?.production === 'NOT_READY').length;
  const ready = rows.filter((x) => x.tracks?.production === 'READY').length;
  const noAck = rows.filter((x) => x.ack && !x.ack.sentAt).length;

  if (loading) return <div className="loading"><div className="spinner" /> Loading sales orders…</div>;

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-title">Sales Orders</div>
          <div className="page-sub">Created only from approved customer POs — quote version locked, five independent status tracks.</div>
        </div>
        <button className="btn primary" onClick={() => nav('/os/pos')}>⇪ PO Inbox</button>
      </div>

      <div className="grid cols-4" style={{ marginBottom: 16 }}>
        <div className="metric-card"><div className="metric-label">Sales orders</div><div className="metric-value" style={{ color: 'var(--ac)' }}>{rows.length}</div><div className="metric-note">OS chain</div></div>
        <div className="metric-card"><div className="metric-label">Production not ready</div><div className="metric-value" style={{ color: 'var(--yl)' }}>{openProd}</div><div className="metric-note">gates incomplete</div></div>
        <div className="metric-card"><div className="metric-label">Ready for production</div><div className="metric-value" style={{ color: 'var(--gn)' }}>{ready}</div><div className="metric-note">all gates green</div></div>
        <div className="metric-card"><div className="metric-label">Ack not sent</div><div className="metric-value" style={{ color: 'var(--or)' }}>{noAck}</div><div className="metric-note">acknowledgments pending</div></div>
      </div>

      <div className="toolbar"><input className="search" placeholder="Search SO, client, PO…" value={q} onChange={(e) => setQ(e.target.value)} /></div>

      <div className="table-wrap"><table>
        <thead><tr><th>SO</th><th>Client</th><th>PO</th><th>Lines</th><th>Total</th><th>Requested</th><th>Status tracks</th></tr></thead>
        <tbody>
          {shown.map((x) => (
            <tr key={x.id} onClick={() => nav(`/os/so?id=${encodeURIComponent(x.id)}`)}>
              <td><strong>{x.soNum}</strong></td>
              <td>{x.customerName}</td>
              <td>{x.poNumber}</td>
              <td>{x.skuCount || x.lineIds?.length || 0}</td>
              <td>{usd(x.total)}</td>
              <td>{fmtDate(x.requestedDate)}</td>
              <td><Tracks tracks={x.tracks} compact /></td>
            </tr>
          ))}
          {!shown.length && <tr><td colSpan={7}><div className="empty">No OS-chain sales orders yet — approve a PO in the inbox and create one.</div></td></tr>}
        </tbody>
      </table></div>

      {rows.length > 0 && <div className="page-sub" style={{ marginTop: 8 }}><Chip label="C" tone="var(--tx3)" /> tracks read left→right: Commercial · Production · Fulfillment · Invoice · Payment</div>}
    </>
  );
}
